import React, { useEffect, useMemo, useRef, useState } from "react";
import { BalaoComentarios } from "../../components/HistoricoAmbiente";
import { EmptyStateErro } from "../../components/EmptyState";
import {
  FiAlertTriangle,
  FiBox,
  FiChevronDown,
  FiClock,
  FiDollarSign,
  FiEye,
  FiEyeOff,
  FiSearch,
} from "react-icons/fi";
import BotaoExportacao from "../../components/Exportacao";
import NavegacaoEtapa from "../../components/NavegacaoEtapa";
import Calendar, { SCALES } from "../../components/Calendar";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import FilterPanel from "../../components/FilterPanel";
import StatusCheckbox from "../../components/StatusCheckbox";
import {
  FilterField,
  FilterGrid,
  FilterLabel,
  FiltroSelect,
} from "../../components/PainelTabela/styles";
import { useDados } from "../../context/DadosContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { formatarData, formatarMoeda, parseNumero } from "../../utils/calculos";
import { plural } from "../../utils/dinheiro";
import {
  getEntregaViewId,
  isEntregaCandidate,
  isEntregaSemLote,
  parseMarceneiro,
  statusAcabamentoAmbiente,
  statusTerceirosAmbiente,
} from "../../utils/entregas";
import { F } from "../../utils/gerencialFields";
import { compareDatesAsc, toDate } from "../../utils/status";
import EntregaDrawer from "./EntregaDrawer";
import ListaCards, { CardEntrega } from "./ListaCards";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";
import { CardList, SoIcone } from "../../components/ListaCompacta/styles";
import {
  EMPTY_VALUE,
  SEM_MARCENEIRO,
  buildLinha,
  buildLinhaPrevisao,
  linhaParaEvento,
  passaNosFiltros,
  previsaoParaEvento,
} from "./linhasEntrega";
import {
  AmbienteCell,
  CalendarioArea,
  CaptionSub,
  CaptionTitle,
  ControlsRight,
  ControlsRow,
  EmptyState,
  EyeToggle,
  FiltroSoIcone,
  Legend,
  LegendItem,
  MarceneiroCell,
  MarceneiroSub,
  MarceneiroText,
  PrevisaoToggle,
  RowAvatar,
  SearchBox,
  SearchIcon,
  SearchInput,
  SemMarceneiro,
  SemPrazoHeader,
  SemPrazoItem,
  SemPrazoList,
  SemPrazoSection,
  StatusBadge,
  Subtitle,
  SummaryCard,
  SummaryGrid,
  SummaryHead,
  SummaryHeadLeft,
  SummaryLabel,
  SummaryPill,
  SummaryRow,
  SummarySub,
  SummaryValue,
  TabButton,
  Tabela,
  TableCaption,
  TableSection,
  TableWrapper,
  Tabs,
  Td,
  Th,
  Title,
  TopBar,
  TopBarActions,
  ViewTab,
  ViewTabs,
  Wrapper,
} from "./styles";

const INITIAL_FILTERS = {
  marceneiro: "TODOS",
  status: "TODOS",
};

const VIEW_MODE_STORAGE_KEY = "pharuerp:entregas:view-mode";
const CALENDAR_SCALE_STORAGE_KEY = "pharuerp:entregas:calendar-scale";
const PREVISOES_SEM_LOTE_STORAGE_KEY = "pharuerp:entregas:calendar-previsoes-sem-lote";
const DEFAULT_CALENDAR_SCALE = "month";
const PREVISOES_SEM_LOTE_AJUDA =
  "Ambientes ainda sem data de otimização e sem número de lote, marcados na " +
  "data de entrega prevista em contrato, com contorno tracejado. Não entram nos cards.";
const PREVISOES_SEM_LOTE_AJUDA_ID = "entregas-previsoes-sem-lote-ajuda";

// View tabs do mockup: Lista + 4 escalas do calendário. Cada aba de
// calendário carrega a escala correspondente do componente Calendar.
const VIEW_TABS = [
  { id: "lista", label: "Lista" },
  { id: "dia", label: "Dia", scale: "day" },
  { id: "semana", label: "Semana", scale: "week" },
  { id: "mes", label: "Mês", scale: "month" },
  { id: "ano", label: "Ano", scale: "year" },
];

function readInitialViewMode() {
  if (typeof window === "undefined") return "lista";
  return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) === "calendario"
    ? "calendario"
    : "lista";
}

function readInitialCalendarScale() {
  if (typeof window === "undefined") return DEFAULT_CALENDAR_SCALE;
  const stored = window.localStorage.getItem(CALENDAR_SCALE_STORAGE_KEY);
  return SCALES.includes(stored) ? stored : DEFAULT_CALENDAR_SCALE;
}

// Desligado por padrao: o calendario continua mostrando so a fila com lote.
function readInitialPrevisoesSemLote() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(PREVISOES_SEM_LOTE_STORAGE_KEY) === "1";
}

const VIEWS = [
  {
    id: "todos",
    label: "Todos",
    predicate: (row) => getEntregaViewId(row) !== null,
  },
  {
    id: "em_maquinas",
    label: "Em máquinas",
    predicate: (row) => getEntregaViewId(row) === "em_maquinas",
  },
  {
    id: "em_marcenaria",
    label: "Em marcenaria",
    predicate: (row) => getEntregaViewId(row) === "em_marcenaria",
  },
  {
    id: "pode_entregar",
    label: "Pode entregar",
    predicate: (row) => getEntregaViewId(row) === "pode_entregar",
  },
];

function normalizeSearch(value) {
  return String(value ?? "").toLowerCase();
}

function iniciais(nome) {
  const partes = String(nome || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

function formatarDiasUteis(dias) {
  if (dias == null) return EMPTY_VALUE;
  return `${Math.round(dias)} du`;
}

export default function PlanejamentoEntregas() {
  const { dados, erro, initialLoading } = useDados();
  const { precosVisiveis, togglePrecos } = usePrecosVisiveis();
  const pageRef = useRef(null);
  const compacto = useMediaQuery(mq.tablet);
  const [activeView, setActiveView] = useState("todos");
  const [busca, setBusca] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });
  const [linhaSelecionada, setLinhaSelecionada] = useState(null);
  const [viewMode, setViewMode] = useState(readInitialViewMode);
  const [calendarScale, setCalendarScale] = useState(readInitialCalendarScale);
  const [previsoesSemLote, setPrevisoesSemLote] = useState(readInitialPrevisoesSemLote);
  const [calendarCursor, setCalendarCursor] = useState(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });
  const [semPrazoOpen, setSemPrazoOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CALENDAR_SCALE_STORAGE_KEY, calendarScale);
  }, [calendarScale]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(PREVISOES_SEM_LOTE_STORAGE_KEY, previsoesSemLote ? "1" : "0");
  }, [previsoesSemLote]);

  const activeViewTab =
    viewMode === "lista"
      ? "lista"
      : VIEW_TABS.find((tab) => tab.scale === calendarScale)?.id || "mes";

  function selectViewTab(tab) {
    if (tab.id === "lista") {
      setViewMode("lista");
    } else {
      setViewMode("calendario");
      setCalendarScale(tab.scale);
    }
  }

  const linhasPorView = useMemo(() => {
    const elegiveis = dados.filter(isEntregaCandidate);
    return VIEWS.reduce((accumulator, view) => {
      accumulator[view.id] = elegiveis
        .filter(view.predicate)
        .map(buildLinha)
        .sort((left, right) => compareDatesAsc(left.dataLimite, right.dataLimite));
      return accumulator;
    }, {});
  }, [dados]);

  const activeRows = useMemo(
    () => linhasPorView[activeView] || [],
    [activeView, linhasPorView]
  );
  const query = normalizeSearch(busca.trim());
  const marceneiroOptions = useMemo(
    () => [
      "TODOS",
      ...[...new Set(activeRows.map((linha) => linha.marceneiro).filter(Boolean))].sort(
        (left, right) =>
          String(left).localeCompare(String(right), "pt-BR", { sensitivity: "base" })
      ),
    ],
    [activeRows]
  );
  const statusOptions = useMemo(
    () => ["TODOS", ...new Set(activeRows.map((linha) => linha.status).filter(Boolean))],
    [activeRows]
  );
  const linhas = activeRows.filter((linha) =>
    passaNosFiltros(linha, { query, ...filters })
  );
  const activeFilterChips = [
    filters.marceneiro !== "TODOS" ? `Marceneiro: ${filters.marceneiro}` : null,
    filters.status !== "TODOS" ? `Status: ${filters.status}` : null,
  ].filter(Boolean);

  const totalAtraso = linhas.filter((linha) => linha.statusVariant === "danger").length;
  const totalSemMarceneiro = linhas.filter((linha) => linha.marceneiro === SEM_MARCENEIRO).length;
  const totalValor = linhas.reduce((total, linha) => total + (parseNumero(linha.valor) || 0), 0);

  const { events: calendarEvents, semPrazo: semPrazoLinhas } = useMemo(() => {
    const events = [];
    const semPrazo = [];
    linhas.forEach((linha) => {
      const evento = linhaParaEvento(linha);
      if (evento) events.push(evento);
      else semPrazo.push(linha);
    });
    return { events, semPrazo };
  }, [linhas]);

  const carregadoEvents = useMemo(() => {
    if (viewMode !== "calendario") return [];
    const events = [];
    dados.forEach((row, index) => {
      const dataCarregamento = toDate(row[F.CARREGAMENTO]);
      if (!dataCarregamento) return;
      const linha = buildLinha(row, index);
      // Carregados nao passam pelo filtro de status: o status e o do prazo
      // de entrega, que ja nao faz sentido para quem carregou.
      if (!passaNosFiltros(linha, { query, marceneiro: filters.marceneiro })) return;
      events.push({
        id: `carregado-${linha.id}`,
        date: dataCarregamento,
        groupLabel: linha.cliente,
        title: linha.ambiente,
        subtitle: "Carregado",
        tone: "neutral",
        payload: linha,
      });
    });
    return events;
  }, [dados, viewMode, filters.marceneiro, query]);

  // Ambientes sem lote (col. S e Y vazias) passam pelos mesmos filtros da
  // fila, mas nao pelas abas de visao — sao uma camada extra do calendario,
  // como os carregados. Sempre calculados no calendario para o contador do
  // seletor dizer quantos entrariam; so viram eventos com o seletor ligado.
  const semLoteLinhas = useMemo(() => {
    if (viewMode !== "calendario") return [];
    return dados
      .map((row, index) => (isEntregaSemLote(row) ? buildLinhaPrevisao(row, index) : null))
      .filter(Boolean)
      .filter((linha) => passaNosFiltros(linha, { query, ...filters }))
      .sort((left, right) => compareDatesAsc(left.dataLimite, right.dataLimite));
  }, [dados, viewMode, query, filters]);

  const { events: previsaoEvents, semPrazo: previsaoSemPrazo } = useMemo(() => {
    const events = [];
    const semPrazo = [];
    if (!previsoesSemLote) return { events, semPrazo };
    semLoteLinhas.forEach((linha) => {
      const evento = previsaoParaEvento(linha);
      if (evento) events.push(evento);
      else semPrazo.push(linha);
    });
    return { events, semPrazo };
  }, [semLoteLinhas, previsoesSemLote]);

  const eventosCalendario = useMemo(
    () => [...calendarEvents, ...carregadoEvents, ...previsaoEvents],
    [calendarEvents, carregadoEvents, previsaoEvents]
  );
  const semPrazoTodas = [...semPrazoLinhas, ...previsaoSemPrazo];

  useEffect(() => {
    if (viewMode !== "calendario") setSemPrazoOpen(false);
  }, [viewMode]);

  const exportResumo = [
    { label: "Ambientes", value: linhas.length, type: "number" },
    { label: "Em atraso", value: totalAtraso, type: "number" },
    { label: "Sem marceneiro", value: totalSemMarceneiro, type: "number" },
    { label: "Valor total", value: totalValor, type: "currency" },
  ];

  const exportSecoes = [
    {
      id: "lista",
      label: "Entregas",
      columns: [
        { key: "cliente", label: "Cliente", type: "text" },
        { key: "ambiente", label: "Ambiente", type: "text" },
        { key: "marceneiro", label: "Marceneiro", type: "text" },
        { key: "numeroLote", label: "Lote", type: "text" },
        { key: "marcenariaTermino", label: "Final marc.", type: "date" },
        { key: "dataReprogramada", label: "Entrega reprogramada", type: "date" },
        { key: "dataLimite", label: "Prazo", type: "date" },
        { key: "status", label: "Status", type: "status" },
        { key: "valor", label: "Valor", type: "currency" },
      ],
      rows: linhas,
    },
  ];

  const exportVisual = {
    ref: pageRef,
    sections:
      viewMode === "calendario"
        ? [
            { id: "resumo", label: "Cards de resumo", selector: "[data-pdf='resumo-entregas']" },
            { id: "calendario", label: "Calendário de entregas", selector: "[data-pdf='calendario-entregas']" },
          ]
        : [
            { id: "resumo", label: "Cards de resumo", selector: "[data-pdf='resumo-entregas']" },
            { id: "lista", label: "Lista de entregas", selector: "[data-pdf='lista-entregas']" },
          ],
  };

  useExportSnapshot(
    "planejamento-entregas",
    {
      titulo: "Planejamento de Entregas",
      // Gate do agendador. `!initialLoading` sozinho nao distingue "carregou"
      // de "falhou": em todo caminho de erro do DadosContext o estado vira
      // dados=[] + erro + loading=false, entao o snapshot saia "pronto" com
      // ZERO linhas — e o Excel/PDF agendado chegava vazio na pasta, sem aviso.
      // E o cenario mais provavel no iframe, que nao tem gesto para pedir
      // permissao do arquivo da planilha.
      pronto: !initialLoading && !erro && dados.length > 0,
      resumo: exportResumo,
      secoes: exportSecoes,
      visual: exportVisual,
    },
    pageRef
  );

  if (initialLoading) {
    return <PageLoader scope="entregas" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  const totalPendentes = linhas.length;
  const mostrarMaquinas = activeView === "em_maquinas";
  const mostrandoPrevisoes = viewMode === "calendario" && previsoesSemLote;
  const colCount = 7;

  return (
    <Wrapper ref={pageRef}>
      <TopBar>
        <NavegacaoEtapa>
          <Title>Planejamento de Entregas</Title>
          <Subtitle>
            Ambientes com lote gerado e ainda sem entrega realizada
            {mostrandoPrevisoes &&
              ` · calendário com ${plural(semLoteLinhas.length, "previsão", "previsões")} sem lote (tracejadas, fora dos cards)`}
          </Subtitle>
        </NavegacaoEtapa>
        <TopBarActions>
          <SearchBox>
            <SearchIcon>
              <FiSearch size={15} />
            </SearchIcon>
            <SearchInput
              type="search"
              aria-label="Busca geral em entregas"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              placeholder="Cliente, ambiente, lote, marceneiro..."
            />
          </SearchBox>
          <FiltroSoIcone>
          <FilterPanel
            compact
            triggerLabel="Marceneiro"
            title="Filtros de entregas"
            meta={`${linhas.length} de ${activeRows.length} ambientes visíveis`}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((current) => !current)}
            onClose={() => setFiltersOpen(false)}
            onReset={() => setFilters({ ...INITIAL_FILTERS })}
            chips={activeFilterChips}
          >
            <FilterGrid>
              <FilterField>
                <FilterLabel>Marceneiro</FilterLabel>
                <FiltroSelect
                  value={filters.marceneiro}
                  onChange={(event) =>
                    setFilters((current) => ({ ...current, marceneiro: event.target.value }))
                  }
                >
                  {marceneiroOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === "TODOS" ? "Todos" : option}
                    </option>
                  ))}
                </FiltroSelect>
              </FilterField>
              <FilterField>
                <FilterLabel>Status</FilterLabel>
                <FiltroSelect
                  value={filters.status}
                  onChange={(event) =>
                    setFilters((current) => ({ ...current, status: event.target.value }))
                  }
                >
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === "TODOS" ? "Todos" : option}
                    </option>
                  ))}
                </FiltroSelect>
              </FilterField>
            </FilterGrid>
          </FilterPanel>
          </FiltroSoIcone>
          <SoIcone>
            <BotaoExportacao
              titulo="Planejamento de Entregas"
              resumo={exportResumo}
              secoes={exportSecoes}
              visual={exportVisual}
              filtrosAtivos={activeFilterChips}
            />
          </SoIcone>
        </TopBarActions>
      </TopBar>

      <SummaryGrid data-pdf="resumo-entregas">
        <SummaryCard>
          <SummaryHead>
            <SummaryHeadLeft>
              <FiBox size={14} />
              <SummaryLabel style={{ margin: 0 }}>Ambientes</SummaryLabel>
            </SummaryHeadLeft>
          </SummaryHead>
          <SummaryRow>
            <SummaryValue>{totalPendentes}</SummaryValue>
            <SummarySub>pendentes</SummarySub>
          </SummaryRow>
        </SummaryCard>

        <SummaryCard $tone={totalAtraso > 0 ? "danger" : "default"}>
          <SummaryHead>
            <SummaryHeadLeft $tone={totalAtraso > 0 ? "danger" : "default"}>
              <FiClock size={14} />
              <SummaryLabel style={{ margin: 0 }}>Em atraso</SummaryLabel>
            </SummaryHeadLeft>
          </SummaryHead>
          <SummaryRow>
            <SummaryValue $tone={totalAtraso > 0 ? "danger" : "default"}>{totalAtraso}</SummaryValue>
            {totalAtraso > 0 ? (
              <SummarySub>ambiente{totalAtraso === 1 ? "" : "s"} atrasado{totalAtraso === 1 ? "" : "s"}</SummarySub>
            ) : (
              <SummaryPill $tone="success">tudo em dia</SummaryPill>
            )}
          </SummaryRow>
        </SummaryCard>

        <SummaryCard $tone={totalSemMarceneiro > 0 ? "warning" : "default"}>
          <SummaryHead>
            <SummaryHeadLeft $tone={totalSemMarceneiro > 0 ? "warning" : "default"}>
              <FiAlertTriangle size={14} />
              <SummaryLabel style={{ margin: 0 }}>Sem marceneiro</SummaryLabel>
            </SummaryHeadLeft>
          </SummaryHead>
          <SummaryRow>
            <SummaryValue $tone={totalSemMarceneiro > 0 ? "warning" : "default"}>
              {totalSemMarceneiro}
            </SummaryValue>
            <SummarySub>aguardando alocação</SummarySub>
          </SummaryRow>
        </SummaryCard>

        <SummaryCard>
          <SummaryHead>
            <SummaryHeadLeft>
              <FiDollarSign size={14} />
              <SummaryLabel style={{ margin: 0 }}>Valor total</SummaryLabel>
            </SummaryHeadLeft>
            <EyeToggle
              type="button"
              data-export-no-print
              onClick={togglePrecos}
              aria-label={precosVisiveis ? "Ocultar valores" : "Mostrar valores"}
              title={precosVisiveis ? "Ocultar valores" : "Mostrar valores"}
            >
              {precosVisiveis ? <FiEye size={15} /> : <FiEyeOff size={15} />}
            </EyeToggle>
          </SummaryHead>
          <SummaryRow>
            <SummaryValue>{precosVisiveis ? formatarMoeda(totalValor) : "R$ ••••••"}</SummaryValue>
          </SummaryRow>
        </SummaryCard>
      </SummaryGrid>

      <ControlsRow>
        <Tabs>
          {VIEWS.map((view) => (
            <TabButton
              key={view.id}
              type="button"
              $active={activeView === view.id}
              onClick={() => {
                setActiveView(view.id);
                setFilters({ ...INITIAL_FILTERS });
              }}
            >
              {view.label}
              <span>{linhasPorView[view.id]?.length || 0}</span>
            </TabButton>
          ))}
        </Tabs>
        <ControlsRight>
          <ViewTabs role="group" aria-label="Modo de visualização">
            {VIEW_TABS.map((tab) => (
              <ViewTab
                key={tab.id}
                type="button"
                $active={activeViewTab === tab.id}
                aria-pressed={activeViewTab === tab.id}
                onClick={() => selectViewTab(tab)}
              >
                {tab.label}
              </ViewTab>
            ))}
          </ViewTabs>
          {viewMode === "calendario" && (
            <PrevisaoToggle $active={previsoesSemLote} title={PREVISOES_SEM_LOTE_AJUDA}>
              <input
                type="checkbox"
                checked={previsoesSemLote}
                aria-describedby={PREVISOES_SEM_LOTE_AJUDA_ID}
                onChange={(event) => setPrevisoesSemLote(event.target.checked)}
              />
              <span>Previsões sem lote</span>
              <em>{semLoteLinhas.length}</em>
              <span id={PREVISOES_SEM_LOTE_AJUDA_ID} className="visually-hidden">
                {PREVISOES_SEM_LOTE_AJUDA}
              </span>
            </PrevisaoToggle>
          )}
        </ControlsRight>
      </ControlsRow>

      {viewMode === "calendario" ? (
        <>
          <CalendarioArea data-pdf="calendario-entregas" data-export-expand>
            <Calendar
              events={eventosCalendario}
              cursor={calendarCursor}
              scale={calendarScale}
              hideScaleSwitcher
              onCursorChange={setCalendarCursor}
              onScaleChange={setCalendarScale}
              onSelectEvent={(eventItem) => setLinhaSelecionada(eventItem.payload)}
              emptyMessage="Nenhuma entrega no período com os filtros atuais."
            />
          </CalendarioArea>
          {semPrazoTodas.length > 0 && (
            <SemPrazoSection>
              <SemPrazoHeader
                type="button"
                $open={semPrazoOpen}
                onClick={() => setSemPrazoOpen((value) => !value)}
                aria-expanded={semPrazoOpen}
              >
                <span>
                  {semPrazoTodas.length}{" "}
                  {semPrazoTodas.length === 1 ? "entrega" : "entregas"} sem prazo definido
                </span>
                <span className="chevron" aria-hidden="true">
                  <FiChevronDown size={16} />
                </span>
              </SemPrazoHeader>
              {semPrazoOpen && compacto && (
                <SemPrazoList>
                  <CardList>
                    {semPrazoTodas.map((linha) => (
                      <CardEntrega key={linha.id} linha={linha} onAbrir={setLinhaSelecionada} />
                    ))}
                  </CardList>
                </SemPrazoList>
              )}
              {semPrazoOpen && !compacto && (
                <SemPrazoList>
                  {semPrazoTodas.map((linha) => (
                    <SemPrazoItem
                      key={linha.id}
                      type="button"
                      onClick={() => setLinhaSelecionada(linha)}
                    >
                      <strong>{linha.cliente}</strong> — {linha.ambiente}
                      {linha.numeroLote && linha.numeroLote !== EMPTY_VALUE && (
                        <> · Lote {linha.numeroLote}</>
                      )}
                      {linha.previsao && <> · sem lote</>}
                    </SemPrazoItem>
                  ))}
                </SemPrazoList>
              )}
            </SemPrazoSection>
          )}
        </>
      ) : compacto ? (
        <ListaCards
          linhas={linhas}
          mostrarMaquinas={mostrarMaquinas}
          onAbrir={setLinhaSelecionada}
          vazio="Nenhum ambiente encontrado nesta visualização."
        />
      ) : (
        <TableSection>
          <TableCaption>
            <div>
              <CaptionTitle>
                {totalPendentes} ambiente{totalPendentes === 1 ? "" : "s"} pendente
                {totalPendentes === 1 ? "" : "s"}
              </CaptionTitle>
              <CaptionSub>
                Ordenados por data de entrega · clique para abrir a linha do tempo
              </CaptionSub>
            </div>
            <Legend>
              <LegendItem $tone="danger">Vence hoje</LegendItem>
              <LegendItem $tone="warning">Vence essa semana</LegendItem>
              <LegendItem $tone="success">Dentro do prazo</LegendItem>
            </Legend>
          </TableCaption>
          <TableWrapper data-pdf="lista-entregas">
            <Tabela $density="compact">
              <thead>
                <tr>
                  <Th $center style={{ width: 84 }}>
                    Receb.
                  </Th>
                  <Th>Cliente</Th>
                  <Th>Ambiente</Th>
                  {mostrarMaquinas ? (
                    <>
                      <Th>Lote</Th>
                      <Th>Chegada de MDFs</Th>
                      <Th $center>Dias na fila</Th>
                    </>
                  ) : (
                    <>
                      <Th>Marceneiro</Th>
                      <Th>Lote</Th>
                      <Th>Entrega</Th>
                    </>
                  )}
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {linhas.length === 0 ? (
                  <tr>
                    <Td colSpan={colCount}>
                      <EmptyState>Nenhum ambiente encontrado nesta visualização.</EmptyState>
                    </Td>
                  </tr>
                ) : (
                  linhas.map((linha) => (
                    <tr
                      key={linha.id}
                      onClick={() => setLinhaSelecionada(linha)}
                      style={{ cursor: "pointer" }}
                    >
                      <Td $center>
                        <div style={{ display: "inline-flex", gap: 6, justifyContent: "center" }}>
                          <StatusCheckbox
                            status={statusTerceirosAmbiente(linha.raw)}
                            title="Itens de Terceiros"
                            tag="T"
                          />
                          <StatusCheckbox
                            status={statusAcabamentoAmbiente(linha.raw)}
                            title="Itens de Acabamento"
                            tag="A"
                          />
                        </div>
                      </Td>
                      <Td>{linha.cliente}</Td>
                      <Td>
                        <AmbienteCell $tone={linha.statusVariant}>
                          <span>{linha.ambiente}</span>
                          <BalaoComentarios row={linha.raw} />
                        </AmbienteCell>
                      </Td>
                      {mostrarMaquinas ? (
                        <>
                          <Td>{linha.numeroLote}</Td>
                          <Td>{linha.chegadaMdf ? formatarData(linha.chegadaMdf) : EMPTY_VALUE}</Td>
                          <Td $center>{formatarDiasUteis(linha.diasNaFilaMaquinas)}</Td>
                        </>
                      ) : (
                        <>
                          <Td>
                            {(() => {
                              const m = parseMarceneiro(linha.marceneiro);
                              if (m.semMarceneiro) {
                                return <SemMarceneiro>Sem marceneiro</SemMarceneiro>;
                              }
                              return (
                                <MarceneiroCell>
                                  <RowAvatar>{iniciais(m.nome)}</RowAvatar>
                                  <MarceneiroText>
                                    <span>{m.nome}</span>
                                    {m.tipo && <MarceneiroSub>{m.tipo}</MarceneiroSub>}
                                  </MarceneiroText>
                                </MarceneiroCell>
                              );
                            })()}
                          </Td>
                          <Td>{linha.numeroLote}</Td>
                          <Td>{formatarData(linha.dataLimite) || EMPTY_VALUE}</Td>
                        </>
                      )}
                      <Td>
                        <StatusBadge $tone={linha.statusVariant}>{linha.status}</StatusBadge>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </Tabela>
          </TableWrapper>
        </TableSection>
      )}

      <EntregaDrawer
        linha={linhaSelecionada}
        precosVisiveis={precosVisiveis}
        onTogglePrecos={togglePrecos}
        onClose={() => setLinhaSelecionada(null)}
      />
    </Wrapper>
  );
}
